import React from 'react';
import { CheckCircleIcon, AlertCircleIcon, HelpCircleIcon, DollarSignIcon } from 'lucide-react';
interface ReconciliationSummaryProps {
  totalSystemTickets: number;
  totalBillingEntries: number;
  matchedCount: number;
  mismatchCount: number;
  unmatchedSystemTickets: number;
  unmatchedBillingEntries: number;
  systemTotal: number;
  billingTotal: number;
  varianceAmount: number;
  onAutoReconcile: () => void;
}
const ReconciliationSummary: React.FC<ReconciliationSummaryProps> = ({
  totalSystemTickets,
  totalBillingEntries,
  matchedCount,
  mismatchCount,
  unmatchedSystemTickets,
  unmatchedBillingEntries,
  systemTotal,
  billingTotal,
  varianceAmount,
  onAutoReconcile
}) => {
  // Calculate percentages for progress bars
  const matchedPercentage = totalSystemTickets > 0 ? Math.round(matchedCount / totalSystemTickets * 100) : 0;
  const mismatchPercentage = totalSystemTickets > 0 ? Math.round(mismatchCount / totalSystemTickets * 100) : 0;
  const unmatchedPercentage = totalSystemTickets > 0 ? Math.round(unmatchedSystemTickets / totalSystemTickets * 100) : 0;
  return <div className="bg-white shadow rounded-lg p-6" data-id="element-576">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6" data-id="element-577">
        <h3 className="text-lg font-medium text-gray-900" data-id="element-578">
          Reconciliation Summary
        </h3>
        <button onClick={onAutoReconcile} className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-579">
          <CheckCircleIcon className="h-5 w-5 mr-1" data-id="element-580" />
          <span data-id="element-581">Auto-Reconcile</span>
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-582">
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-583">
          <div className="flex items-center" data-id="element-584">
            <DollarSignIcon className="h-8 w-8 text-gray-400 mr-3" data-id="element-585" />
            <div data-id="element-586">
              <p className="text-sm text-gray-500 mb-1" data-id="element-587">Total Variance</p>
              <p className={`text-xl font-bold ${varianceAmount !== 0 ? 'text-red-600' : 'text-green-600'}`} data-id="element-588">
                ${Math.abs(varianceAmount).toFixed(2)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-green-50 rounded-lg p-4 border border-green-200" data-id="element-589">
          <div className="flex items-center" data-id="element-590">
            <CheckCircleIcon className="h-8 w-8 text-green-500 mr-3" data-id="element-591" />
            <div data-id="element-592">
              <p className="text-sm text-green-700 mb-1" data-id="element-593">Matched</p>
              <p className="text-xl font-bold text-green-800" data-id="element-594">{matchedCount}</p>
              <p className="text-xs text-green-600" data-id="element-595">
                of {totalSystemTickets} tickets
              </p>
            </div>
          </div>
        </div>
        <div className="bg-red-50 rounded-lg p-4 border border-red-200" data-id="element-596">
          <div className="flex items-center" data-id="element-597">
            <AlertCircleIcon className="h-8 w-8 text-red-500 mr-3" data-id="element-598" />
            <div data-id="element-599">
              <p className="text-sm text-red-700 mb-1" data-id="element-600">Mismatched</p>
              <p className="text-xl font-bold text-red-800" data-id="element-601">{mismatchCount}</p>
              <p className="text-xs text-red-600" data-id="element-602">Amount discrepancies</p>
            </div>
          </div>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200" data-id="element-603">
          <div className="flex items-center" data-id="element-604">
            <HelpCircleIcon className="h-8 w-8 text-yellow-500 mr-3" data-id="element-605" />
            <div data-id="element-606">
              <p className="text-sm text-yellow-700 mb-1" data-id="element-607">Unmatched</p>
              <p className="text-xl font-bold text-yellow-800" data-id="element-608">
                {unmatchedSystemTickets} / {unmatchedBillingEntries}
              </p>
              <p className="text-xs text-yellow-600" data-id="element-609">System / Billing</p>
            </div>
          </div>
        </div>
      </div>
      <div className="space-y-4 mb-6" data-id="element-610">
        <div data-id="element-611">
          <div className="flex justify-between text-sm mb-1" data-id="element-612">
            <span className="font-medium text-gray-700" data-id="element-613">
              Reconciliation Progress
            </span>
            <span className="text-gray-500" data-id="element-614">
              {matchedCount + mismatchCount} of {totalSystemTickets} processed
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5 mb-1" data-id="element-615">
            <div className="bg-green-500 h-2.5 rounded-full" style={{
            width: `${matchedPercentage}%`
          }} data-id="element-616"></div>
          </div>
          <div className="flex justify-between text-xs text-gray-500" data-id="element-617">
            <span data-id="element-618">{matchedPercentage}% Matched</span>
            <span data-id="element-619">{unmatchedPercentage}% Remaining</span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-620">
          <div data-id="element-621">
            <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-622">
              System Total
            </h4>
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-100" data-id="element-623">
              <p className="text-2xl font-bold text-blue-800" data-id="element-624">
                ${systemTotal.toFixed(2)}
              </p>
              <p className="text-xs text-blue-600" data-id="element-625">
                {totalSystemTickets} tickets in system
              </p>
            </div>
          </div>
          <div data-id="element-626">
            <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-627">
              Billing Total
            </h4>
            <div className="bg-purple-50 rounded-lg p-4 border border-purple-100" data-id="element-628">
              <p className="text-2xl font-bold text-purple-800" data-id="element-629">
                ${billingTotal.toFixed(2)}
              </p>
              <p className="text-xs text-purple-600" data-id="element-630">
                {totalBillingEntries} entries in billing
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="border-t border-gray-200 pt-4" data-id="element-631">
        <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-632">
          Reconciliation Tips
        </h4>
        <ul className="text-sm text-gray-600 space-y-1 list-disc pl-5" data-id="element-633">
          <li data-id="element-634">
            Use "Auto-Reconcile" to automatically match tickets by number and
            amount
          </li>
          <li data-id="element-635">For mismatched amounts, review both system and billing data</li>
          <li data-id="element-636">Manually match tickets by selecting one from each side</li>
          <li data-id="element-637">Unmatch any incorrectly matched tickets to retry</li>
        </ul>
      </div>
    </div>;
};
export default ReconciliationSummary;